import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { getBranchPLReport, downloadBranchPLPDF } from '../../api/reports';
import { getBranches } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';
import ReportPreviewModal from '../../components/reports/ReportPreviewModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BranchPLReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const now = new Date();
  const [dateRange, setDateRange] = useState({
    start: formatDateToISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDateToISO(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await getBranches();
        let branchesArray = [];
        if (Array.isArray(response)) {
          branchesArray = response;
        } else if (Array.isArray(response?.branches)) {
          branchesArray = response.branches;
        } else if (Array.isArray(response?.data?.branches)) {
          branchesArray = response.data.branches;
        } else if (Array.isArray(response?.data)) {
          branchesArray = response.data;
        }
        setBranches(branchesArray);
      } catch (error) {
        toast.error('Failed to load branches');
      }
    };
    fetchBranches();
  }, []);

  const fetchReport = useCallback(async () => {
    if (!selectedBranch) return;

    setLoading(true);
    try {
      const data = await getBranchPLReport(selectedBranch, dateRange.start, dateRange.end);
      setReport(data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, dateRange]);

  useEffect(() => {
    if (selectedBranch) {
      fetchReport();
    }
  }, [selectedBranch, fetchReport]);

  const selectedBranchData = branches.find((b) => b.id == selectedBranch);
  const netProfit = report?.summary?.net_profit || 0;
  const isProfit = netProfit >= 0;

  const handleDownloadPDF = async () => {
    if (!selectedBranch) return;
    setDownloading(true);
    try {
      await downloadBranchPLPDF(selectedBranch, dateRange.start, dateRange.end);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!selectedBranch || !report) return;
    setSendingWhatsApp(true);
    try {
      // Create a summary message for WhatsApp
      const branchName = selectedBranchData?.name || 'Branch';
      const message = encodeURIComponent(
        `📊 *${branchName} P&L Report*\n` +
          `📅 Period: ${dateRange.start} to ${dateRange.end}\n\n` +
          `💰 Revenue: ${formatCurrency(report.summary.revenue)}\n` +
          `📦 COGS: ${formatCurrency(report.summary.cogs)}\n` +
          `👥 Payroll: ${formatCurrency(report.summary.payroll_expense)}\n` +
          `${isProfit ? '✅' : '❌'} Net ${isProfit ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(netProfit))}\n` +
          `📈 Margin: ${report.summary.profit_margin_percent?.toFixed(1)}%`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error('Failed to open WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Content for modal preview
  const ReportContent = () => (
    <>
      {/* Summary Section */}
      <div className="mb-6">
        <h3 className="font-bold text-lg mb-3">P&L Summary: {selectedBranchData?.name}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600">Revenue</p>
            <p className="text-lg font-bold text-green-700">
              {formatCurrency(report?.summary?.revenue)}
            </p>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">COGS</p>
            <p className="text-lg font-bold text-orange-700">
              {formatCurrency(report?.summary?.cogs)}
            </p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600">Payroll + Pension</p>
            <p className="text-lg font-bold text-red-700">
              {formatCurrency(
                parseFloat(report?.summary?.payroll_expense || 0) +
                  parseFloat(report?.summary?.employer_pension || 0)
              )}
            </p>
          </div>
          <div className={`p-3 rounded-lg ${isProfit ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <p className={`text-sm ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
              Net {isProfit ? 'Profit' : 'Loss'}
            </p>
            <p className={`text-lg font-bold ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(Math.abs(netProfit))}
            </p>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {report?.category_breakdown?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-bold mb-3">Sales by Category</h3>
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-center">Sales</th>
                <th className="px-3 py-2 text-center">Units</th>
                <th className="px-3 py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {report.category_breakdown.map((cat, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2 capitalize">{cat.category}</td>
                  <td className="px-3 py-2 text-center">{cat.sale_count}</td>
                  <td className="px-3 py-2 text-center">{cat.units_sold}</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {formatCurrency(cat.revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Monthly Trend */}
      {report?.monthly_trend?.length > 0 && (
        <div>
          <h3 className="font-bold mb-3">Monthly Trend</h3>
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Month</th>
                <th className="px-3 py-2 text-center">Sales</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {report.monthly_trend.map((month, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2">
                    {new Date(month.month).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="px-3 py-2 text-center">{month.sale_count}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(month.revenue)}</td>
                  <td
                    className={`px-3 py-2 text-right font-semibold ${parseFloat(month.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(month.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Branch P&L Report
                </h1>
                <p className="text-sm text-gray-500">Profit & Loss by branch</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <button onClick={fetchReport} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
              {report && (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
                >
                  <FileText className="w-4 h-4" />
                  Generate Report
                </button>
              )}
            </div>
          </div>
        </div>

        {!selectedBranch ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Select a branch to view its P&L report</p>
          </div>
        ) : loading ? (
          <LoadingSpinner message="Generating P&L report..." className="py-12" />
        ) : report ? (
          <>
            {/* P&L Summary */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
              <h3 className="font-semibold mb-4">P&L Summary: {selectedBranchData?.name}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Revenue</p>
                  <p className="text-xl font-bold text-green-700">
                    <BlurredValue value={formatCurrency(report.summary.revenue)} />
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600 mb-1">Cost of Goods</p>
                  <p className="text-xl font-bold text-orange-700">
                    <BlurredValue value={formatCurrency(report.summary.cogs)} />
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 mb-1">Payroll + Pension</p>
                  <p className="text-xl font-bold text-red-700">
                    <BlurredValue
                      value={formatCurrency(
                        parseFloat(report.summary.payroll_expense || 0) +
                          parseFloat(report.summary.employer_pension || 0)
                      )}
                    />
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isProfit ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <p className={`text-sm mb-1 ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                    Net {isProfit ? 'Profit' : 'Loss'}
                  </p>
                  <p
                    className={`text-xl font-bold ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}
                  >
                    <BlurredValue value={formatCurrency(Math.abs(netProfit))} />
                  </p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span>Profit Margin:</span>
                  <span className="font-semibold">
                    {report.summary.profit_margin_percent.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Total Sales:</span>
                  <span className="font-semibold">{report.summary.sale_count}</span>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            {report.category_breakdown?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
                <h3 className="font-semibold mb-4">Sales by Category</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Category</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Sales</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Units</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.category_breakdown.map((cat, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium capitalize">{cat.category}</td>
                          <td className="px-4 py-3 text-center">{cat.sale_count}</td>
                          <td className="px-4 py-3 text-center">{cat.units_sold}</td>
                          <td className="px-4 py-3 text-right font-semibold">
                            <BlurredValue value={formatCurrency(cat.revenue)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Monthly Trend */}
            {report.monthly_trend?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="font-semibold mb-4">Monthly Trend</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Month</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Sales</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Revenue</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Profit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.monthly_trend.map((month, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">
                            {new Date(month.month).toLocaleDateString('en-US', {
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="px-4 py-3 text-center">{month.sale_count}</td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(month.revenue)} />
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-semibold ${parseFloat(month.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                          >
                            <BlurredValue value={formatCurrency(month.profit)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Branch P&L Report"
        subtitle={`${selectedBranchData?.name || 'Branch'} | ${dateRange.start} to ${dateRange.end}`}
        onDownload={handleDownloadPDF}
        onWhatsApp={handleWhatsApp}
        downloading={downloading}
        sendingWhatsApp={sendingWhatsApp}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default BranchPLReport;
